import React, { createContext, useContext, useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';
import { supabase, setupAxiosAuth } from './supabaseClient';
import { MetaPixel } from './utils/metaPixel';

// Set up axios to include Supabase Bearer token automatically
setupAxiosAuth(axios);

// Lazy-loaded pages (code splitting)
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Discover = lazy(() => import('./pages/Discover'));
const Artist = lazy(() => import('./pages/Artist'));
const Album = lazy(() => import('./pages/Album'));
const Store = lazy(() => import('./pages/Store'));
const Playlists = lazy(() => import('./pages/Playlists'));
const PlaylistDetail = lazy(() => import('./pages/PlaylistDetail'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const ArtistDashboard = lazy(() => import('./pages/ArtistDashboard'));
const ArtistEarnings = lazy(() => import('./pages/ArtistEarnings'));
const ArtistAnalytics = lazy(() => import('./pages/ArtistAnalytics'));
const AdminEarnings = lazy(() => import('./pages/AdminEarnings'));
const Search = lazy(() => import('./pages/Search'));
const Library = lazy(() => import('./pages/Library'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const TrackDetail = lazy(() => import('./pages/TrackDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const PaymentHistory = lazy(() => import('./pages/PaymentHistory'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Cart = lazy(() => import('./pages/Cart'));
const NewsletterUnsubscribe = lazy(() => import('./pages/NewsletterUnsubscribe'));
const PrivacyPolicy = lazy(() => import('./pages/policies/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/policies/Terms'));
const ArtistAgreement = lazy(() => import('./pages/policies/ArtistAgreement'));
const RefundPolicy = lazy(() => import('./pages/policies/RefundPolicy'));
const CopyrightPolicy = lazy(() => import('./pages/policies/CopyrightPolicy'));
const CookiePolicy = lazy(() => import('./pages/policies/CookiePolicy'));
import { MaintenancePage } from './components/MaintenancePage';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { useCart } from './hooks/useCart';
import { AddToPlaylistProvider } from './contexts/AddToPlaylistContext';

// Components
import { MusicPlayer } from './components/MusicPlayer';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import AdSenseLoader from './components/AdSenseLoader';
import { GenresProvider } from './contexts/GenresContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Player Context
const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) throw new Error('usePlayer must be used within PlayerProvider');
  return context;
};

// Cart Context
const CartContext = createContext(null);
export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCartContext must be used within CartProvider');
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(null);

  const fetchAppUser = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(response.data);
      // Tag Sentry events with the authenticated user so we can trace a bug
      // back to the account that hit it (email + id only, no PII beyond that).
      try {
        Sentry.setUser({ id: response.data.user_id, email: response.data.email, role: response.data.role });
      } catch { /* sentry inert when no DSN */ }
      return response.data;
    } catch (err) {
      // Surface the underlying problem so we don't silently strand the user on
      // /login after a "Welcome back" toast. 401 = token rejected by backend
      // (stale Supabase session in localStorage, mismatched JWT secret, user
      // deleted, etc). Anything else = network / CORS / 500.
      const status = err?.response?.status;
      if (status === 401) {
        console.warn('[auth] /auth/me returned 401 — token is invalid or stale.');
      } else {
        console.error('[auth] /auth/me failed:', status || err?.message || err);
      }
      setUser(null);
      return null;
    }
  }, []);

  const checkMaintenance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/maintenance-status`);
      setMaintenanceMode(res.data);
    } catch {
      setMaintenanceMode({ enabled: false, message: '' });
    }
  }, []);

  useEffect(() => {
    // Skip auth check if we're on the OAuth callback page
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      // If impersonating, use the impersonation token (not the admin's Supabase session)
      const isImpersonating = localStorage.getItem('gospeleon_impersonating');
      const fallbackToken = localStorage.getItem('sb_access_token_fallback');
      if (isImpersonating && fallbackToken) {
        await fetchAppUser(fallbackToken);
      } else {
        let session = null;
        try {
          ({ data: { session } } = await supabase.auth.getSession());
        } catch (e) {
          // Two known causes: (1) malformed persisted token (sign out and
          // let the user re-login), (2) Navigator LockManager contention
          // across tabs — the token is still valid, just can't be read via
          // the lock-wrapped path. Only sign out on genuine malformed cases.
          const msg = String(e?.message || e || '');
          const isLockContention = msg.toLowerCase().includes('lock');
          console.warn('[auth] supabase.auth.getSession threw:', msg);
          if (!isLockContention) {
            try { await supabase.auth.signOut(); } catch { /* already cleared */ }
          }
        }
        if (session?.access_token) {
          const appUser = await fetchAppUser(session.access_token);
          // Token existed but backend rejected it (401) → it's stale. Sign out
          // of Supabase so we don't loop on the same bad token, and so the
          // user sees a clean /login next time.
          if (!appUser) {
            console.warn('[auth] Stale Supabase session detected — clearing.');
            try { await supabase.auth.signOut(); } catch { /* ignore */ }
          }
        } else if (fallbackToken) {
          await fetchAppUser(fallbackToken);
        }
      }
      await checkMaintenance();
      setLoading(false);
    };

    initAuth();

    // Listen for Supabase auth state changes (token refresh, sign out).
    // While impersonating, ignore Supabase events entirely — they come from
    // the admin's still-persisted session and would clobber the impersonated
    // user with the admin on a refresh tick.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (localStorage.getItem('gospeleon_impersonating')) return;
      if (event === 'SIGNED_OUT') {
        setUser(null);
      } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        await fetchAppUser(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchAppUser, checkMaintenance]);

  const login = async (email, password) => {
    // Sign in via Supabase
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // Map unhelpful Supabase errors to user-friendly messages
      const raw = (error.message || '').toLowerCase();
      let friendly = error.message || 'Login failed. Please try again.';
      if (raw.includes('invalid login') || raw.includes('invalid credentials')) {
        friendly = 'Incorrect email or password. Please try again.';
      } else if (
        raw.includes('body has already been consumed') ||
        raw.includes('body stream already read') ||
        raw.includes("failed to execute 'json'") ||
        raw.includes('failed to fetch')
      ) {
        friendly = 'Incorrect email or password. Please try again.';
      } else if (raw.includes('email not confirmed')) {
        friendly = 'Please confirm your email address before signing in.';
      } else if (raw.includes('too many requests') || raw.includes('rate limit')) {
        friendly = 'Too many login attempts. Please wait a moment and try again.';
      } else if (raw.includes('network')) {
        friendly = 'Network error. Check your connection and try again.';
      }
      throw { response: { data: { detail: friendly } } };
    }
    // Fetch full app user from backend
    const appUser = await fetchAppUser(data.session?.access_token);
    if (!appUser) {
      // Supabase auth succeeded but our backend couldn't materialize the user
      // (token rejected, /auth/me network failure, etc). Sign out so we don't
      // sit in a half-authenticated state, then surface a real error to the
      // Login page instead of letting the "Welcome back" toast + redirect
      // loop kick in silently.
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      throw { response: { data: { detail: 'Signed in, but the server couldn\'t load your account. Try refreshing the page or clearing site data.' } } };
    }
    await checkMaintenance();
    return appUser;
  };

  const register = async (email, password, name, role, extraFields = {}) => {
    // Include referral code if present
    const ref = localStorage.getItem('gospeleon_ref');
    if (ref) extraFields.referral_code = ref;
    // Call backend register (creates Supabase auth user + public.users)
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role, ...extraFields });
    const { access_token, refresh_token, ...userData } = response.data;
    // Now sign in via Supabase to establish the client session
    if (access_token) {
      await supabase.auth.setSession({ access_token, refresh_token });
    } else {
      await supabase.auth.signInWithPassword({ email, password });
    }
    if (ref) localStorage.removeItem('gospeleon_ref');
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { Sentry.setUser(null); } catch { /* inert */ }
    await supabase.auth.signOut();
    localStorage.removeItem('sb_access_token_fallback');
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    const { access_token, ...userData } = response.data;
    // Store token for subsequent requests
    if (access_token) {
      localStorage.setItem('sb_access_token_fallback', access_token);
    }
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    // getSession may throw under Navigator LockManager contention (multiple
    // tabs open); fall back to the persisted token so a refresh still works.
    const token = await (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token || null;
      } catch {
        return null;
      }
    })() || localStorage.getItem('sb_access_token_fallback');
    if (token) {
      await fetchAppUser(token);
    }
  };

  const switchBack = async () => {
    try {
      // Restore admin session from localStorage
      const adminToken = localStorage.getItem('gospeleon_admin_token');
      localStorage.removeItem('gospeleon_admin_token');
      localStorage.removeItem('gospeleon_impersonating');
      if (adminToken) {
        localStorage.setItem('sb_access_token_fallback', adminToken);
      }
      // Also call backend to clear cookie state
      await axios.post(`${API}/admin/switch-back`).catch(() => {});
      window.location.href = '/admin';
    } catch {
      localStorage.removeItem('gospeleon_admin_token');
      localStorage.removeItem('gospeleon_impersonating');
      window.location.href = '/admin';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, exchangeSession, refreshUser, maintenanceMode, switchBack }}>
      {children}
    </AuthContext.Provider>
  );
}

function PlayerProvider({ children }) {
  const { user } = useAuth();
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Daily-free-tier quota. `null` = unknown / unlimited (premium / guest).
  // Signed-in non-premium fans get a fixed budget from the backend
  // (/api/streams/quota). Once remaining hits 0 the player switches to
  // preview mode + shows the limit-reached CTA.
  const [remainingQuotaSeconds, setRemainingQuotaSeconds] = useState(null);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [quotaLimitSeconds, setQuotaLimitSeconds] = useState(null);

  const isPremium = user?.subscription_tier?.startsWith('premium');
  const isGuest = !user;

  // Use a ref for playlist state to avoid stale closures entirely
  const playlistRef = useRef({ list: [], index: 0 });

  const refreshQuota = useCallback(async () => {
    if (isGuest || isPremium) {
      setRemainingQuotaSeconds(null);
      setDailyLimitReached(false);
      setQuotaLimitSeconds(null);
      return;
    }
    try {
      const token = (await import('./supabaseClient')).getAccessToken
        ? await (await import('./supabaseClient')).getAccessToken()
        : null;
      const r = await axios.get(`${API}/streams/quota`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const remaining = r.data.remaining_seconds;
      setRemainingQuotaSeconds(remaining);
      setQuotaLimitSeconds(r.data.limit_seconds);
      setDailyLimitReached(remaining === 0);
    } catch {
      // Fail-open: treat as full quota so the player doesn't lock users out
      // over a transient API error.
      setRemainingQuotaSeconds(null);
      setDailyLimitReached(false);
    }
  }, [isGuest, isPremium]);

  useEffect(() => { refreshQuota(); }, [refreshQuota]);

  // Consume `seconds` from the local quota; if the budget hits 0, flip the
  // limit-reached flag so the next playTrack falls back to preview mode.
  const consumeQuota = useCallback((seconds) => {
    if (isGuest || isPremium) return;
    setRemainingQuotaSeconds((prev) => {
      if (prev === null || prev === undefined) return prev;
      const next = Math.max(0, prev - seconds);
      if (next === 0) setDailyLimitReached(true);
      return next;
    });
  }, [isGuest, isPremium]);

  const playTrack = (track, trackList = [], opts = {}) => {
    // For singles (or any standalone track played without a list), seed the
    // queue with the track itself so the loop logic in playNext kicks in.
    const list = trackList.length > 0 ? trackList : [track];
    const idx = list.findIndex(t => t.track_id === track.track_id);
    playlistRef.current = { list, index: idx >= 0 ? idx : 0 };
    setCurrentTrack(track);
    setIsPlaying(true);
    // Preview mode is derived — guests always get previews; signed-in
    // fans who've burned their daily budget also drop to previews;
    // premium and quota-holding fans play full length. Callers can
    // pass { forceFull: true } to bypass the gate for purchased tracks.
    const shouldPreview = !opts.forceFull && !isPremium && !track.is_free_preview && (isGuest || dailyLimitReached);
    setIsPreviewMode(shouldPreview);
  };

  const playNext = useCallback(() => {
    const { list, index } = playlistRef.current;
    if (list.length === 0) return;
    if (list.length === 1) {
      // Single-track loop: re-emit a fresh object reference so the player
      // useEffect re-fires and restarts playback from 0.
      setCurrentTrack({ ...list[0] });
      return;
    }
    const nextIndex = (index + 1) % list.length;
    playlistRef.current.index = nextIndex;
    setCurrentTrack(list[nextIndex]);
  }, []);

  const playPrevious = useCallback(() => {
    const { list, index } = playlistRef.current;
    if (list.length === 0) return;
    if (list.length === 1) {
      setCurrentTrack({ ...list[0] });
      return;
    }
    const prevIndex = index <= 0 ? list.length - 1 : index - 1;
    playlistRef.current.index = prevIndex;
    setCurrentTrack(list[prevIndex]);
  }, []);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const stopPlaying = () => {
    setIsPlaying(false);
    setCurrentTrack(null);
    playlistRef.current = { list: [], index: 0 };
  };

  return (
    <PlayerContext.Provider value={{
      currentTrack,
      fullTrackList: playlistRef.current.list,
      isPlaying,
      isPreviewMode,
      remainingQuotaSeconds,
      dailyLimitReached,
      quotaLimitSeconds,
      refreshQuota,
      consumeQuota,
      playTrack,
      playNext,
      playPrevious,
      togglePlayPause,
      stopPlaying,
      setIsPlaying
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

function ProtectedRoute({ children, artistOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#F59E0B]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (artistOnly && user.role !== 'artist') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#F59E0B]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading, maintenanceMode } = useAuth();

  // Capture referral code from URL (?ref=CODE)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('gospeleon_ref', ref.toUpperCase());
    }
  }, [location.search]);

  // Link referral code to user account when logged in
  useEffect(() => {
    const ref = localStorage.getItem('gospeleon_ref');
    if (ref && user && !loading) {
      axios.post(`${API}/affiliate/link`, { code: ref })
        .then(() => localStorage.removeItem('gospeleon_ref'))
        .catch(() => {}); // silently fail if already linked or invalid
    }
  }, [user, loading]);

  // Meta Pixel — track page views on SPA route changes
  useEffect(() => {
    MetaPixel.pageView();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#F59E0B]"></div>
      </div>
    );
  }

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Maintenance mode gate: block non-admin users
  // During maintenance, only allow login page (for admin access) - block everything else
  if (maintenanceMode?.enabled && user?.role !== 'admin') {
    const allowedDuringMaintenance = ['/login'];
    if (!allowedDuringMaintenance.includes(location.pathname)) {
      return <MaintenancePage message={maintenanceMode.message} />;
    }
  }

  return (
    <>
      <ImpersonationBanner />
      <AdSenseLoader />
      <div className={user?.impersonated_by ? 'pb-10' : ''}>
        <Suspense fallback={<div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#F59E0B]"></div></div>}>
        <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/search" element={<Search />} />
      <Route path="/artist/:artistSlug" element={<Artist />} />
      <Route path="/:artistSlug/album/:albumSlug" element={<Album />} />
      <Route path="/:artistSlug/track/:trackSlug" element={<TrackDetail />} />
      <Route path="/:artistSlug/single/:singleSlug" element={<TrackDetail />} />
      <Route path="/store" element={<Store />} />
      <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/playlists" element={<ProtectedRoute><Playlists /></ProtectedRoute>} />
      <Route path="/playlists/:playlistId" element={<ProtectedRoute><PlaylistDetail /></ProtectedRoute>} />
      <Route path="/artist-dashboard" element={<ProtectedRoute artistOnly><ArtistDashboard /></ProtectedRoute>} />
      <Route path="/artist-earnings" element={<ProtectedRoute artistOnly><ArtistEarnings /></ProtectedRoute>} />
      <Route path="/artist-analytics" element={<ProtectedRoute artistOnly><ArtistAnalytics /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/earnings" element={<AdminRoute><AdminEarnings /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
      <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/artist-agreement" element={<ArtistAgreement />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/copyright-policy" element={<CopyrightPolicy />} />
      <Route path="/copyright" element={<CopyrightPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </>
  );
}

function CartProvider({ children }) {
  const cart = useCart();
  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>;
}

function App() {
  return (
    <div className="dark">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <GenresProvider>
          <CartProvider>
          <AddToPlaylistProvider>
          <PlayerProvider>
            <AppRouter />
            <MusicPlayer />
            <CookieConsentBanner />
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: '#141417',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff'
                }
              }}
            />
          </PlayerProvider>
          </AddToPlaylistProvider>
          </CartProvider>
          </GenresProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
export { API };
